import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import { convertToRaw, convertFromHTML, ContentState } from 'draft-js';

export const isValidEmail = (email: string): boolean => {
  var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const minutesConvertToHours = (numberOfMinutes: number) => {
  const hours = numberOfMinutes / 60;
  let rhours = ('0' + Math.floor(hours)).slice(-2);
  const minutes = (hours - Number(rhours)) * 60;
  let rminutes = ('0' + Math.round(minutes)).slice(-2);
  return { rhours, rminutes };
};

export const hoursConvertToMinutes = (hours: number, minutes: number) => {
  const minutesFromHours = hours * 60;
  const numberOfMinutes = minutesFromHours + minutes;
  return numberOfMinutes;
};

export const ucWords = (str: string) => {
  return (str.toLowerCase() + '').replace(/^(.)|\s+(.)/g, function($1) {
    return $1.toUpperCase();
  });
};

export const getUnique = (arr: any[], comp: string) => {
  const unique = arr
    .map((e: any) => e[comp])
    .map((e, i, final) => final.indexOf(e) === i && i)
    .filter((e: any) => arr[e])
    .map((e: any) => arr[e]);

  return unique;
};

//get new Date with SG timezone
export const getNewDate = (date?: Date) => {
  return new Date(
    date ? new Date(date).toLocaleString('en-US', { timeZone: 'Asia/Singapore' }) : new Date().toLocaleString('en-US', { timeZone: 'Asia/Singapore' })
  );
};

//convert Date to SG timezone
export const convertTz = (date: string | Date) => {
  return zonedTimeToUtc(date, 'Asia/Singapore');
};

export const convertUTC = (value: string) => {
  const date = utcToZonedTime(new Date(value as string).toISOString(), 'UTC');
  return date;
};

export const scheduleLabelGenerate = (scheduleData: ScheduleModel) => {
  let label = '';
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const weeks = ['1st', '2nd', '3rd', '4rd'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const currentRepeatType =
    scheduleData.repeatType === 'Yearly'
      ? 'Years'
      : scheduleData.repeatType === 'Weekly'
      ? 'Weeks'
      : scheduleData.repeatType === 'Monthly'
      ? 'Months'
      : 'Days';

  const every = currentRepeatType ? currentRepeatType : 'Days';
  const repeatOnType = scheduleData.repeatOnDate < 1 ? 'byDay' : 'byDate';

  let repeatOnDayLabel = '';
  let repeatOnWeekLabel = '';
  if (repeatOnType === 'byDay') {
    scheduleData.repeatOnDate = 0;
    if (scheduleData.repeatType === 'Monthly' || scheduleData.repeatType === 'Yearly') {
      repeatOnDayLabel = days[Number(scheduleData.repeatOnDay) - 1];
      repeatOnWeekLabel = weeks[scheduleData.repeatOnWeek - 1];
    }
  }

  if (every === 'Days') {
    label = `Every ${scheduleData.repeatEvery} Days, ${
      scheduleData.repeatEndType === 'AFTER'
        ? scheduleData.repeatEndAfter + ' Times'
        : 'End on ' + format(new Date(scheduleData.repeatEndOnDate), 'dd-MM-yyyy')
    }`;
  } else if (every === 'Weeks') {
    const currentRepeatOnDayLabel: any = [];
    repeatOnDayLabel = '';
    scheduleData.repeatOnDay.split(',').map(day => {
      currentRepeatOnDayLabel.push(days[Number(day) - 1]);
      return day;
    });
    repeatOnDayLabel = currentRepeatOnDayLabel.join(',');
    label = `Every ${scheduleData.repeatEvery} Weeks on ${repeatOnDayLabel}, ${
      scheduleData.repeatEndType === 'AFTER'
        ? scheduleData.repeatEndAfter + ' Times'
        : 'End on ' + format(new Date(scheduleData.repeatEndOnDate), 'dd-MM-yyyy')
    }`;
  } else if (every === 'Months') {
    label = `Every ${scheduleData.repeatEvery} Months on ${
      repeatOnType === 'byDate' ? scheduleData.repeatOnDate + ' Date' : repeatOnWeekLabel + ' ' + repeatOnDayLabel!
    }, ${
      scheduleData.repeatEndType === 'AFTER'
        ? scheduleData.repeatEndAfter + ' Times'
        : 'End on ' + format(new Date(scheduleData.repeatEndOnDate), 'MM-yyyy')
    }`;
  } else if (every === 'Years') {
    const monthLabel = months[scheduleData.repeatOnMonth - 1];
    label = `Every ${scheduleData.repeatEvery} Years on ${monthLabel} ${
      repeatOnType === 'byDate' ? scheduleData.repeatOnDate + ' Date' : repeatOnWeekLabel + repeatOnDayLabel!
    }, ${
      scheduleData.repeatEndType === 'AFTER'
        ? scheduleData.repeatEndAfter + ' Times'
        : 'End on ' + format(new Date(scheduleData.repeatEndOnDate), 'yyyy')
    }`;
  } else {
    label = '-';
  }

  return label;
};

export const combineAddress = (address: string, floorNo: string, unitNo: string, postalCode: string) => {
  // Normalize and avoid duplicating postal code if already present in `address`.
  const base = address ? address.trim() : '';

  let cleanedBase = base;
  if (postalCode && cleanedBase.endsWith(postalCode)) {
    cleanedBase = cleanedBase.slice(0, cleanedBase.length - postalCode.length).trim();
    if (cleanedBase.endsWith(',')) cleanedBase = cleanedBase.slice(0, cleanedBase.length - 1).trim();
  }

  const floorUnit = floorNo || unitNo ? `#${floorNo || ''}${floorNo && unitNo ? '-' : ''}${unitNo || ''}` : '';
  const parts: string[] = [];
  if (cleanedBase) parts.push(cleanedBase);
  if (floorUnit) parts.push(floorUnit);
  if (postalCode) parts.push(postalCode);

  return parts.join(', ');
};

export const getNumberWithOrdinal = (value: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = value % 100;
  return value + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const disablePrevDates = (startDate: Date) => {
  const startSeconds = Date.parse(startDate.toDateString());
  return (date: any) => {
    return Date.parse(date) < startSeconds;
  };
};

export const convertHtml = (value: string) => {
  const contentHTMLValue = convertFromHTML(value);

  // 2. Create the ContentState object
  const stateValue = ContentState.createFromBlockArray(contentHTMLValue.contentBlocks, contentHTMLValue.entityMap);

  // 3. Stringify `state` object from a Draft.Model.Encoding.RawDraftContentState object
  const contentValue = JSON.stringify(convertToRaw(stateValue));

  return contentValue;
};

export const hasAccessPermission = (module: string, functionName: string, roleGrants: any[]) => {
  return roleGrants.some(grant => grant.module === module && grant.function === functionName && grant.isActive === true);
};
export interface SmartRankingSetting {
  skill: boolean;
  proximity: boolean;
  relationship: boolean;
}

export const getSmartRankingSettingValue = (skillOn: boolean, proximityOn: boolean, relationshipOn: boolean): string => {
  if (skillOn && proximityOn && relationshipOn) return 'ALL';
  if (skillOn && proximityOn) return 'SKILL_PROXIMITY';
  if (skillOn && relationshipOn) return 'SKILL_RELATIONSHIP';
  if (proximityOn && relationshipOn) return 'PROXIMITY_RELATIONSHIP';
  if (skillOn) return 'SKILL';
  if (proximityOn) return 'PROXIMITY';
  if (relationshipOn) return 'RELATIONSHIP';
  return 'NONE';
};

export const parseSmartRankingSettingValue = (value?: string): SmartRankingSetting => {
  switch ((value || '').toUpperCase()) {
    case 'ALL':
      return { skill: true, proximity: true, relationship: true };

    case 'SKILL_PROXIMITY':
    case 'BOTH': // backward compatibility
      return { skill: true, proximity: true, relationship: false };

    case 'SKILL_RELATIONSHIP':
      return { skill: true, proximity: false, relationship: true };

    case 'PROXIMITY_RELATIONSHIP':
      return { skill: false, proximity: true, relationship: true };

    case 'SKILL':
      return { skill: true, proximity: false, relationship: false };

    case 'PROXIMITY':
      return { skill: false, proximity: true, relationship: false };

    case 'RELATIONSHIP':
      return { skill: false, proximity: false, relationship: true };

    default:
      return { skill: false, proximity: false, relationship: false };
  }
};

/**
 * react-csv v2 wraps every field in double quotes but never escapes the quotes already
 * inside the value, so `3/8" Plywood` closes its own field and pushes the rest of the row
 * into the next columns. Doubling the quotes up front makes the emitted field RFC 4180 valid.
 * Lone CR are normalised to LF so multi-line descriptions do not split a row either.
 */
export const escapeCsvValue = (value: any): any => {
  if (typeof value === 'string') {
    return value.replace(/\r\n?/g, '\n').replace(/"/g, '""');
  }

  if (Array.isArray(value)) {
    return value.map(escapeCsvValue);
  }

  if (value !== null && typeof value === 'object') {
    return Object.keys(value).reduce((escaped: any, key) => {
      escaped[key] = escapeCsvValue(value[key]);
      return escaped;
    }, {});
  }

  return value;
};

export const escapeCsvData = <T>(rows: T[]): T[] => rows.map(row => escapeCsvValue(row));

// a quotation can only be deleted (together with its invoice) while nothing is paid on it: no invoice, UNPAID, or a $0 FULLY PAID one.
// accepts one value (detail page) or the array_agg arrays of the quotation list
export const isQuotationDeletable = (invoiceStatus: any, invoiceCollectedAmount: any): boolean => {
  const isNothingCollected = ([] as any[]).concat(invoiceCollectedAmount).every(amount => !Number(amount));

  return ([] as any[])
    .concat(invoiceStatus)
    .filter(Boolean)
    .every(status => status === 'UNPAID' || (status === 'FULLY PAID' && isNothingCollected));
};

export const getDeleteQuotationMessage = (hasInvoice: boolean, isSeparateQuotation: boolean): string =>
  'Please confirm that you want to permanently delete this quotation.\n' +
  (isSeparateQuotation ? '' : 'This action will also remove all associated jobs.\n') +
  (hasInvoice ? 'The invoice of this quotation will also be deleted.\n' : '') +
  'Are you sure you want to proceed?';
